import type { ReactNode } from 'react';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import ArticleHero from '../../components/news/ArticleHero';

interface BookSubsection {
  heading: string;
  paragraphs: string[];
}

interface BookSection {
  heading: string;
  image: string;
  imageAlt: string;
  imageContain?: boolean;
  paragraphs: string[];
  subsections?: BookSubsection[];
}

interface BookContent {
  kicker: string;
  title: string;
  lead: string;
  publicationDate: string;
  breadcrumbs: { label: string; href?: string }[];
  sections: BookSection[];
  bibliographyHeading: string;
  bibliography: string[][];
  cta: string;
  ctaAriaLabel: string;
}

const AMAZON_URL = 'https://www.amazon.co.jp/dp/4058027606';
const BODY_TEXT = "font-['Noto_Serif_JP',serif] text-[17px] sm:text-[18px] leading-[1.9] text-[#2a2a2a]";
const BUTTON = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";

function SectionHeading({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

export default function BookDetail({ content: c, bookLd, breadcrumbLd }: {
  content: BookContent;
  bookLd: object;
  breadcrumbLd: object;
}) {
  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(bookLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

      <ArticleHero category={c.kicker} title={c.title} subtitle={c.lead} date={c.publicationDate} image="/images/book/jba-book-billboard-20260915.jpg" />

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={c.breadcrumbs} />
      </div>

      <div className="bg-white">
        <article className="w-full max-w-[760px] mx-auto px-6 pt-12 pb-20">
          {c.sections.map((section, sectionIndex) => (
            <section key={section.heading} className={sectionIndex === 0 ? '' : 'mt-16'}>
              <SectionHeading id={`chapter-${sectionIndex + 1}`}>{section.heading}</SectionHeading>
              <figure className="my-9">
                <div className={section.imageContain ? 'bg-[#f8f6f0] p-6 sm:p-10' : ''}>
                  <img
                    src={section.image}
                    alt={section.imageAlt}
                    loading={sectionIndex === 0 ? 'eager' : 'lazy'}
                    className={section.imageContain ? 'w-full h-[420px] sm:h-[560px] object-contain' : 'w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]'}
                  />
                </div>
              </figure>
              <div className="space-y-6">
                {section.paragraphs.map((paragraph) => <p key={paragraph} className={BODY_TEXT}>{paragraph}</p>)}
              </div>

              {sectionIndex === 0 && (
                <div className="mt-10">
                  <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl mb-5">{c.bibliographyHeading}</h3>
                  <div className="overflow-x-auto border-t-2 border-[#1a2942]">
                    <table className="w-full min-w-[560px] border-collapse font-['Noto_Serif_JP',serif]">
                      <tbody className="text-[14px] sm:text-[15px] text-[#4a4638]">
                        {c.bibliography.map(([label, value]) => (
                          <tr key={label} className="border-b border-[#e2ddd2]">
                            <th scope="row" className="w-36 text-left py-3.5 pr-4 text-[#8f7a56] font-medium tracking-[0.06em]">{label}</th>
                            <td className="py-3.5 px-3">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-8">
                    <a href={AMAZON_URL} target="_blank" rel="noopener noreferrer" className={BUTTON} aria-label={c.ctaAriaLabel}>
                      {c.cta}<i className="ri-arrow-right-line ml-3" aria-hidden="true" />
                    </a>
                  </div>
                </div>
              )}

              {section.subsections?.map((subsection) => (
                <div key={subsection.heading} className="mt-10">
                  <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl mb-5">{subsection.heading}</h3>
                  <div className="space-y-6">
                    {subsection.paragraphs.map((paragraph) => <p key={paragraph} className={BODY_TEXT}>{paragraph}</p>)}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </article>
      </div>
    </Layout>
  );
}
